import { useEffect, useRef, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import styles from "./styles.module.scss";
import {
  StructureNode,
  loadStructure,
  saveStructure,
  newNodeId,
  addChildToNode,
  removeStructureNode,
  updateStructureNode,
} from "./treeUtils";
import { collectPeople, loadTree } from "../OrgChart";

const DRAG_TYPE = "ORG_NODE";

// ── Tree helpers ──────────────────────────────────────────────────────────────

function collectIds(node: StructureNode): Set<string> {
  const ids = new Set<string>();
  const collect = (n: StructureNode) => {
    ids.add(n.id);
    n.children.forEach(collect);
  };
  collect(node);
  return ids;
}

function findNode(nodes: StructureNode[], id: string): StructureNode | null {
  for (const n of nodes) {
    if (n.id === id) return n;
    const found = findNode(n.children, id);
    if (found) return found;
  }
  return null;
}

function moveNode(tree: StructureNode[], dragId: string, targetId: string): StructureNode[] {
  const dragged = findNode(tree, dragId);
  if (!dragged) return tree;
  const withoutDragged = removeStructureNode(tree, dragId);
  return addChildToNode(withoutDragged, targetId, dragged);
}

function reorderChild(
  tree: StructureNode[],
  nodeId: string,
  dir: "up" | "down",
): StructureNode[] {
  const reorder = (nodes: StructureNode[]): StructureNode[] => {
    const idx = nodes.findIndex((n) => n.id === nodeId);
    if (idx === -1) return nodes.map((n) => ({ ...n, children: reorder(n.children) }));
    const result = [...nodes];
    const swapIdx = dir === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= result.length) return result;
    [result[idx], result[swapIdx]] = [result[swapIdx], result[idx]];
    return result;
  };
  return reorder(tree);
}

// ── TreeNode ──────────────────────────────────────────────────────────────────

function collectAllIds(nodes: StructureNode[]): Set<string> {
  const ids = new Set<string>();
  const visit = (n: StructureNode) => { ids.add(n.id); n.children.forEach(visit); };
  nodes.forEach(visit);
  return ids;
}

interface NodeProps {
  node: StructureNode;
  allNodes: StructureNode[];
  depth: number;
  siblings: StructureNode[];
  sibIdx: number;
  expandedIds: Set<string>;
  selectedId: string | null;
  editingId: string | null;
  editDraft: string;
  inputRef: React.RefObject<HTMLInputElement>;
  onSelect: (id: string) => void;
  onToggle: (id: string) => void;
  onDrop: (dragId: string, targetId: string) => void;
  onStartEdit: (id: string, name: string) => void;
  onCommitEdit: (id: string) => void;
  onCancelEdit: () => void;
  onSetDraft: (s: string) => void;
  onAddChild: (parentId: string) => void;
  onAddRoot: () => void;
  onRemove: (id: string) => void;
  onMove: (id: string, dir: "up" | "down") => void;
}

const TreeNode = ({
  node,
  allNodes,
  depth,
  siblings,
  sibIdx,
  expandedIds,
  selectedId,
  editingId,
  editDraft,
  inputRef,
  onSelect,
  onToggle,
  onDrop,
  onStartEdit,
  onCommitEdit,
  onCancelEdit,
  onSetDraft,
  onAddChild,
  onAddRoot,
  onRemove,
  onMove,
}: NodeProps) => {
  const isEditing = editingId === node.id;
  const isFirst = sibIdx === 0;
  const isLast = sibIdx === siblings.length - 1;
  const hasChildren = node.children.length > 0;
  const isExpanded = expandedIds.has(node.id);
  const isSelected = selectedId === node.id;

  const [{ isDragging }, dragRef] = useDrag({
    type: DRAG_TYPE,
    item: { id: node.id },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const [{ isOver, canDrop }, dropRef] = useDrop<
    { id: string },
    void,
    { isOver: boolean; canDrop: boolean }
  >({
    accept: DRAG_TYPE,
    canDrop: (item) => {
      if (item.id === node.id) return false;
      // Prevent dropping a node onto its own descendant (would cause data loss)
      const dragSource = findNode(allNodes, item.id);
      if (!dragSource) return false;
      return !collectIds(dragSource).has(node.id);
    },
    drop: (item, monitor) => {
      if (monitor.didDrop()) return;
      onDrop(item.id, node.id);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
      canDrop: monitor.canDrop(),
    }),
  });

  const rowRef = (el: HTMLDivElement | null) => {
    dropRef(el);
  };

  const handleRef = (el: HTMLSpanElement | null) => {
    dragRef(el);
  };

  const isDropActive = isOver && canDrop;

  return (
    <div>
      <div
        ref={rowRef}
        className={`${styles.nodeRow} ${isSelected ? styles.nodeRowSelected : ""} ${isDropActive ? styles.dropTarget : ""} ${isDragging ? styles.draggingSource : ""}`}
        style={{ paddingLeft: depth * 20 }}
        onClick={() => onSelect(node.id)}
      >
        <span
          ref={handleRef}
          className={styles.dragHandle}
          title="Przeciągnij, aby zmienić powiązanie"
        >
          ⠿
        </span>

        <button
          className={styles.toggleBtn}
          onClick={() => onToggle(node.id)}
          style={{ visibility: hasChildren ? "visible" : "hidden" }}
          tabIndex={-1}
        >
          {isExpanded ? "▼" : "▶"}
        </button>

        {isEditing ? (
          <input
            ref={inputRef}
            className={styles.nameInput}
            value={editDraft}
            onChange={(e) => onSetDraft(e.target.value)}
            onBlur={() => onCommitEdit(node.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onCommitEdit(node.id);
              if (e.key === "Escape") onCancelEdit();
            }}
            autoFocus
          />
        ) : (
          <span
            className={styles.nodeName}
            onDoubleClick={() => onStartEdit(node.id, node.name)}
            title="Kliknij dwukrotnie, aby edytować"
          >
            {node.name}
          </span>
        )}

        <div className={styles.nodeActions} onClick={(e) => e.stopPropagation()}>
          <button
            className={styles.orderBtn}
            onClick={() => onMove(node.id, "up")}
            disabled={isFirst}
            title="Przesuń w górę"
          >
            ▲
          </button>
          <button
            className={styles.orderBtn}
            onClick={() => onMove(node.id, "down")}
            disabled={isLast}
            title="Przesuń w dół"
          >
            ▼
          </button>
          {depth === 0 && (
            <button
              className={styles.addSiblingBtn}
              onClick={onAddRoot}
              title="Dodaj kolejny element na poziomie głównym"
            >
              + poziom
            </button>
          )}
          <button
            className={styles.addChildBtn}
            onClick={() => onAddChild(node.id)}
            title="Dodaj element podrzędny"
          >
            + pod.
          </button>
          <button
            className={styles.removeBtn}
            onClick={() => onRemove(node.id)}
            title="Usuń element"
          >
            ✕
          </button>
        </div>
      </div>

      {isExpanded && node.children.map((child, i) => (
        <TreeNode
          key={child.id}
          node={child}
          allNodes={allNodes}
          depth={depth + 1}
          siblings={node.children}
          sibIdx={i}
          expandedIds={expandedIds}
          selectedId={selectedId}
          editingId={editingId}
          editDraft={editDraft}
          inputRef={inputRef}
          onSelect={onSelect}
          onToggle={onToggle}
          onDrop={onDrop}
          onStartEdit={onStartEdit}
          onCommitEdit={onCommitEdit}
          onCancelEdit={onCancelEdit}
          onSetDraft={onSetDraft}
          onAddChild={onAddChild}
          onAddRoot={onAddRoot}
          onRemove={onRemove}
          onMove={onMove}
        />
      ))}
    </div>
  );
};

// ── StructureEditor ───────────────────────────────────────────────────────────

const StructureEditor = () => {
  const [tree, setTree] = useState<StructureNode[]>(() => loadStructure());
  const [expandedIds, setExpandedIds] = useState<Set<string>>(
    () => collectAllIds(loadStructure()),
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [cardNameDraft, setCardNameDraft] = useState("");
  const [cardShortNameDraft, setCardShortNameDraft] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedNode = selectedId ? findNode(tree, selectedId) : null;

  // OrgChart data for Kierownik / Odpowiedzialny selects
  const orgPeople = collectPeople(loadTree());
  const orgSupervisors = loadTree()[0]?.children.filter((c) => c.type === "position") ?? [];

  useEffect(() => {
    if (!selectedNode) return;
    setCardNameDraft(selectedNode.name);
    setCardShortNameDraft(selectedNode.shortName ?? "");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const persist = (next: StructureNode[]) => {
    setTree(next);
    saveStructure(next);
  };

  const handleSelect = (id: string) => {
    if (!findNode(tree, id)) return;
    setSelectedId(id);
  };

  const handleCardUpdate = (patch: Partial<StructureNode>) => {
    if (!selectedId) return;
    persist(updateStructureNode(tree, selectedId, (n) => ({ ...n, ...patch })));
  };

  const commitCardName = () => {
    const trimmed = cardNameDraft.trim();
    if (trimmed) handleCardUpdate({ name: trimmed });
  };

  const commitCardShortName = () => {
    handleCardUpdate({ shortName: cardShortNameDraft.trim() || undefined });
  };

  const handleToggle = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDrop = (dragId: string, targetId: string) => {
    persist(moveNode(tree, dragId, targetId));
  };

  const startEdit = (id: string, currentName: string) => {
    setEditDraft(currentName);
    setEditingId(id);
    setTimeout(() => inputRef.current?.select(), 0);
  };

  const commitEdit = (id: string) => {
    const trimmed = editDraft.trim();
    if (trimmed)
      persist(updateStructureNode(tree, id, (n) => ({ ...n, name: trimmed })));
    setEditingId(null);
  };

  const handleAddChild = (parentId: string) => {
    const childId = newNodeId();
    persist(
      addChildToNode(tree, parentId, {
        id: childId,
        name: "Nowy element",
        expanded: true,
        children: [],
      }),
    );
    setExpandedIds((prev) => new Set([...prev, parentId, childId]));
  };

  const handleAddRoot = () => {
    persist([
      ...tree,
      { id: newNodeId(), name: "Nowy element", expanded: true, children: [] },
    ]);
  };

  const handleRemove = (id: string) => {
    persist(removeStructureNode(tree, id));
    if (selectedId === id || (selectedId && collectIds(findNode(tree, id) ?? { id: "", name: "", expanded: true, children: [] }).has(selectedId))) {
      setSelectedId(null);
    }
  };

  const handleMove = (id: string, dir: "up" | "down") => {
    persist(reorderChild(tree, id, dir));
  };

  const allNodes = tree;

  return (
    <div className={styles.root}>
      <div className={styles.hint}>
        Zbuduj strukturę organizacyjną zespołu. Przeciągaj węzły na inny węzeł,
        aby zmienić powiązanie. Strzałki ▲▼ zmieniają kolejność w ramach
        tego samego poziomu.
      </div>

      <button className={styles.addRootBtn} onClick={handleAddRoot}>
        + Dodaj element główny
      </button>

      <div className={styles.contentArea}>
        <div className={styles.tree}>
          {tree.length === 0 ? (
            <div className={styles.empty}>
              Brak elementów. Kliknij „+ Dodaj element główny", aby zacząć.
            </div>
          ) : (
            tree.map((node, i) => (
              <TreeNode
                key={node.id}
                node={node}
                allNodes={allNodes}
                depth={0}
                siblings={tree}
                sibIdx={i}
                expandedIds={expandedIds}
                selectedId={selectedId}
                editingId={editingId}
                editDraft={editDraft}
                inputRef={inputRef}
                onSelect={handleSelect}
                onToggle={handleToggle}
                onDrop={handleDrop}
                onStartEdit={startEdit}
                onCommitEdit={commitEdit}
                onCancelEdit={() => setEditingId(null)}
                onSetDraft={setEditDraft}
                onAddChild={handleAddChild}
                onAddRoot={handleAddRoot}
                onRemove={handleRemove}
                onMove={handleMove}
              />
            ))
          )}
        </div>

        {selectedNode ? (
          <div className={styles.nodeCard}>
            <div className={styles.nodeCardHeader}>
              <span className={styles.nodeCardLabel}>Jednostka organizacyjna</span>
              <button className={styles.nodeCardClose} onClick={() => setSelectedId(null)}>✕</button>
            </div>
            <div className={styles.nodeCardBody}>
              <TextField
                label="Nazwa jednostki"
                value={cardNameDraft}
                onChange={(e) => setCardNameDraft(e.target.value)}
                onBlur={commitCardName}
                onKeyDown={(e) => { if (e.key === "Enter") commitCardName(); }}
                variant="standard"
                fullWidth
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <button
                          className={styles.confirmBtn}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={commitCardName}
                        >
                          ✓
                        </button>
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <TextField
                label="Skrót nazwy"
                value={cardShortNameDraft}
                onChange={(e) => setCardShortNameDraft(e.target.value)}
                onBlur={commitCardShortName}
                onKeyDown={(e) => { if (e.key === "Enter") commitCardShortName(); }}
                variant="standard"
                fullWidth
                inputProps={{ maxLength: 32 }}
                helperText="Skrót nazwy jednostki używany na schemacie"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <button
                          className={styles.confirmBtn}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={commitCardShortName}
                        >
                          ✓
                        </button>
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <FormControl variant="standard" fullWidth>
                <InputLabel>Kierownik</InputLabel>
                <Select
                  value={selectedNode.managerId ?? ""}
                  label="Kierownik"
                  onChange={(e) => handleCardUpdate({ managerId: e.target.value || undefined })}
                >
                  <MenuItem value=""><em>Brak</em></MenuItem>
                  {orgPeople.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}{p.position ? ` – ${p.position}` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {orgSupervisors.length > 0 && (
                <FormControl variant="standard" fullWidth>
                  <InputLabel>Odpowiedzialny (Zarząd)</InputLabel>
                  <Select
                    value={selectedNode.supervisorId ?? ""}
                    label="Odpowiedzialny (Zarząd)"
                    onChange={(e) => handleCardUpdate({ supervisorId: e.target.value || undefined })}
                  >
                    <MenuItem value=""><em>Brak</em></MenuItem>
                    {orgSupervisors.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name}{s.personFullName ? ` – ${s.personFullName}` : ""}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

            </div>
          </div>
        ) : (
          <div className={styles.nodeCardEmpty}>
            <span>Kliknij element struktury,<br />aby wyświetlić szczegóły</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default StructureEditor;
